import React, { ReactNode, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Container, Title } from '@mantine/core';
import './LandingPage.css';
import HeroSection from './HeroSection';
import FeatureAccordion from './FeatureAccordion';
import FeatureList from './FeatureList';
import PricingSection from './PricingSection';
import ComparisonChart from './ComparisonChart';
import ValueProposition from './ValueProposition';
import BeforeAndAfter from './BeforeAndAfter';
import { SECTION_PATH_TO_ID } from './sectionPaths';
import PublicFooter from '../../components/PublicFooter/PublicFooter';
import { useAuthRedirect } from '../../hooks/useAuthRedirect';

const JumboTitle = ({
  children,
  order = 1,
  fz = 'xl',
  ...otherProps
}: {
  children: ReactNode;
  order?: 1 | 2 | 3 | 4 | 5;
  fz?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  [key: string]: any;
}) => {
  const FONT_WEIGHT_MAP: Record<string, number> = {
    xl: 800,
    lg: 800,
    md: 700,
    sm: 600,
    xs: 500,
  };

  const FONT_SIZE_MAP: Record<string, string> = {
    xl: 'clamp(2.5rem, 5vw + 1rem, 4rem)',
    lg: 'clamp(2rem, 4vw + 1rem, 3.5rem)',
    md: 'clamp(1.75rem, 3vw + 1rem, 3rem)',
    sm: 'clamp(1.5rem, 2.5vw + 1rem, 2.5rem)',
    xs: 'clamp(1.25rem, 2vw + 1rem, 2rem)',
  };

  const { style: otherStyle, ...restProps } = otherProps;

  return (
    <Title
      fw={FONT_WEIGHT_MAP[fz]}
      lh={1}
      order={order}
      style={{
        fontSize: FONT_SIZE_MAP[fz],
        ...otherStyle,
      }}
      {...restProps}
    >
      {children}
    </Title>
  );
};

const LandingPage = () => {
  const authState = useAuthRedirect();
  const location = useLocation();
  const isRendered = authState === 'unauthenticated';

  useEffect(() => {
    const sectionId = SECTION_PATH_TO_ID[location.pathname];
    if (!sectionId || !isRendered) return;

    const scrollToSection = () => {
      const el = document.getElementById(sectionId);
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    // Wait for images/fonts to load so layout is stable before computing scroll
    // target — otherwise the section can shift down mid-animation and overshoot.
    if (document.readyState === 'complete') {
      const rafId = requestAnimationFrame(scrollToSection);
      return () => cancelAnimationFrame(rafId);
    }
    window.addEventListener('load', scrollToSection);
    return () => window.removeEventListener('load', scrollToSection);
  }, [location.pathname, isRendered]);

  // Show nothing while checking auth state or redirecting
  if (!isRendered) {
    return null;
  }

  return (
    <div className='landing-page'>
      <HeroSection />
      <Container
        id='features'
        size='lg'
        pt='xs'
        pb='xs'
        className='accordion-title-section'
      >
        <JumboTitle
          order={2}
          fz='md'
          ta='center'
          style={{ textWrap: 'balance' }}
        >
          Intake, billing, documents, and AI.
          <br />
          All working together on day one.
        </JumboTitle>
      </Container>
      <FeatureAccordion />
      <FeatureList />
      <PricingSection />
      <ComparisonChart />
      <BeforeAndAfter />
      {/* ScrollFeatures - Hidden for now, but keeping code for potential future use */}
      {/* <ScrollFeatures /> */}
      <ValueProposition />
      <PublicFooter />
    </div>
  );
};

export default LandingPage;
